import React, { useCallback, useEffect, useReducer } from 'react';
import { useTranslation } from 'react-i18next';
import { useParams } from 'react-router-dom';
import { useService } from '../../services/services';
import { can, downloadFile } from '../../services/utils';
import { FileCard, IFolderItem } from './components/list-item';
import Modal from '../../components/modals/modal';
import FileUploadModal from './components/file-upload-modal';
import CreateOrEditModal from './components/create-or-edit-modal';
import { SearchIcon } from './components/icons';
import css from './add-file.module.css';
import DocumentStorageFile from '../../interfaces/document-storage-file';
import OptionModal from '../../components/modals/option-modal';
import { useSelector } from 'react-redux';
import { RootState } from '../../store/store';

const BASE_CLASS = 'add-file';

interface IAddFile {
  item: IFolderItem;
  onFolderUpdate: (updatedFolder: IFolderItem) => void;
}

interface State {
  files: DocumentStorageFile[];
  filteredFiles: DocumentStorageFile[];
  editFile: DocumentStorageFile;
  openFileUploadModal: boolean;
  openDeleteModal: boolean;
  openEditFileModal: boolean;
}

interface Action {
  type: string;
  payload?: DocumentStorageFile[];
}

const initialState: State = {
  files: [],
  filteredFiles: [],
  editFile: null,
  openFileUploadModal: false,
  openDeleteModal: false,
  openEditFileModal: false,
};

const ACTIONS = {
  SET_FILES: 'SET_FILES',
  SET_FILTERED_FILES: 'SET_FILTERED_FILES',
  TOGGLE_MODAL: 'TOGGLE_MODAL',
  TOGGLE_DELETE_MODAL: 'TOGGLE_DELETE_MODAL',
  SET_EDIT_FILE: 'SET_EDIT_FILE',
  TOGGLE_EDIT_FILE_MODAL: 'TOGGLE_EDIT_FILE_MODAL',
} as const;

function reducer(state: State, action: Action): State {
  switch (action.type) {
    case ACTIONS.SET_FILES:
      return { ...state, files: action.payload, filteredFiles: action.payload };
    case ACTIONS.SET_FILTERED_FILES:
      return { ...state, filteredFiles: action.payload };
    case ACTIONS.TOGGLE_MODAL:
      return { ...state, openFileUploadModal: !state.openFileUploadModal };
    case ACTIONS.TOGGLE_DELETE_MODAL:
      return { ...state, openDeleteModal: !state.openDeleteModal };
    case ACTIONS.SET_EDIT_FILE:
      return { ...state, editFile: action?.payload?.[0] ?? null };
    case ACTIONS.TOGGLE_EDIT_FILE_MODAL:
      return { ...state, openEditFileModal: !state.openEditFileModal };
    default:
      return state;
  }
}

const AddFile: React.FC<IAddFile> = ({ item, onFolderUpdate }) => {
  const { t } = useTranslation();
  const { documentStorageService } = useService();
  const { farmId, tab } = useParams<{ [key: string]: string }>();
  const [state, dispatch] = useReducer(reducer, initialState);
  const { farmUser } = useSelector((state: RootState) => ({
    farmUser: state.global.farmUser,
  }));
  const canCreate = can(farmUser, { title: `create document_storage_${tab}` });
  const canEdit = can(farmUser, { title: `update document_storage_${tab}` });
  const canDelete = can(farmUser, { title: `delete document_storage_${tab}` });

  const handleSearch = (event: React.ChangeEvent<HTMLInputElement>) => {
    const search = event.target.value;
    const filtered = state.files.filter((file) =>
      file.name.toLowerCase().includes(search.toLowerCase())
    );
    dispatch({ type: ACTIONS.SET_FILTERED_FILES, payload: filtered });
  };

  const handleModal = (type: string = '') => {
    switch (type) {
      case 'delete':
        dispatch({ type: ACTIONS.TOGGLE_DELETE_MODAL });
        break;
      case 'edit':
        dispatch({ type: ACTIONS.TOGGLE_EDIT_FILE_MODAL });
        break;
      case 'create':
        dispatch({ type: ACTIONS.TOGGLE_MODAL });
        break;
    }
  };

  const handleUploadFiles = async (files: File[]) => {
    const response = await documentStorageService
      .uploadFiles(farmId, tab, item?.name, files)
      .toPromise();

    dispatch({ type: ACTIONS.SET_FILES, payload: response.files });
    onFolderUpdate(response.folder);
  };

  const handleCancel = () => {
    dispatch({ type: ACTIONS.SET_EDIT_FILE, payload: null });
    handleModal('delete');
  };

  const handleEditSubmit = async (data: { name: string }) => {
    try {
      let response = await documentStorageService
        .editFile(farmId, tab, item?.name, state.editFile.name, data.name)
        .toPromise();
      dispatch({ type: ACTIONS.SET_FILES, payload: response.files });
      dispatch({ type: ACTIONS.SET_EDIT_FILE, payload: null });
      dispatch({ type: ACTIONS.TOGGLE_EDIT_FILE_MODAL });
    } catch (error) {
      console.error('Error creating/editing folder:', error);
    }
  };

  const handleDeleteFile = async (name: string) => {
    try {
      const response = await documentStorageService
        .deleteFile(farmId, tab, item?.name, name)
        .toPromise();
      dispatch({ type: ACTIONS.SET_FILES, payload: response.files });
      dispatch({ type: ACTIONS.SET_EDIT_FILE, payload: null });
      dispatch({ type: ACTIONS.TOGGLE_DELETE_MODAL });
      onFolderUpdate(response.folder);
    } catch (error) {
      console.error('Error deleting folder:', error);
    }
  };

  const download = useCallback(
    async (fileName: string) => {
      const response = (await documentStorageService
        .download(farmId, tab, item.name, fileName)
        .toPromise()) as Blob;
      downloadFile(response, fileName);
    },
    [documentStorageService, farmId, tab, item.name]
  );

  const fetchFiles = useCallback(async () => {
    try {
      const response = await documentStorageService
        .getFiles(farmId, tab, item?.name)
        .toPromise();
      dispatch({ type: ACTIONS.SET_FILES, payload: response?.files });
    } catch (error) {
      console.error('Error fetching folders:', error);
    }
  }, [documentStorageService, farmId, tab, item.name]);

  useEffect(() => {
    fetchFiles();
  }, [fetchFiles]);

  const renderModalContent = () => {
    if (state.openDeleteModal) {
      return (
        <OptionModal
          title={t('pages.document_storage.delete_file_title')}
          className={css['delete-modal-wrapper']}
          buttons={[
            {
              title: t('Yes'),
              name: 'yes',
              value: true,
              ignoreValidation: true,
            },
            {
              title: t('No'),
              name: 'no',
              value: false,
              ignoreValidation: true,
            },
          ]}
          onChoice={(choice) => {
            if (choice) {
              handleDeleteFile(state.editFile.name);
            } else {
              handleCancel();
            }
          }}
          onClose={() => handleCancel()}
        />
      );
    }

    if (state.openEditFileModal) {
      return (
        <CreateOrEditModal
          onClose={() => handleModal('edit')}
          value={state.editFile}
          editItem={state.editFile}
          items={state.files}
          handleOnSubmit={handleEditSubmit}
          title={t('pages.document_storage.rename_file')}
          label={t('pages.document_storage.new_file_name')}
          submitTitle={t('edit')}
        />
      );
    }
    return (
      <FileUploadModal
        onClose={() => handleModal('create')}
        handleUploadFiles={handleUploadFiles}
      />
    );
  };

  const data = state.filteredFiles.length ? state.filteredFiles : state.files;

  return (
    <div className={css[`${BASE_CLASS}-container`]}>
      <div className={css[`${BASE_CLASS}-header`]}>
        <h2>{t('pages.document_storage.attached_files')}</h2>
        <div className={css[`${BASE_CLASS}-header-buttons`]}>
          <div className={css[`${BASE_CLASS}-search-wrapper`]}>
            <input
              type="text"
              placeholder={t('search')}
              onChange={handleSearch}
              className={css[`${BASE_CLASS}-search`]}
            />
            <img src={SearchIcon} alt="Search Icon" />
          </div>
          {canCreate && (
            <button
              className={css[`${BASE_CLASS}-button`]}
              onClick={() => handleModal('create')}
            >
              {t('pages.document_storage.add_file')}
            </button>
          )}
        </div>
      </div>
      {data?.map((fileItem, idx) => (
        <FileCard
          key={fileItem?.name + idx}
          item={fileItem}
          download={download}
          handleModal={handleModal}
          setEditFile={(file) =>
            dispatch({ type: ACTIONS.SET_EDIT_FILE, payload: [file] })
          }
          canDelete={canDelete}
          canEdit={canEdit}
        />
      ))}
      <Modal
        visible={
          state.openFileUploadModal ||
          state.openDeleteModal ||
          state.openEditFileModal
        }
        onClose={handleModal}
        body={renderModalContent()}
        buttons={null}
      />
    </div>
  );
};

export default AddFile;
